import React, { useState, useEffect } from 'react';
import { Container, Box, CircularProgress, Typography, Alert } from '@mui/material';
import PageHeader from '../components/PageHeader';
import { useNavigate } from 'react-router-dom';
import FeedbackCard from '../components/FeedbackCard';
import { useFeedbackService, FeedbackItem } from '../services/feedbackService';

const FeedbackPage: React.FC = () => {
  const navigate = useNavigate();

  function handleBack(): void {
    navigate(-1);
  }

  const [feedbackData, setFeedbackData] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { fetchFeedback } = useFeedbackService();

  useEffect(() => {
    const loadFeedback = async () => {
      try {
        setLoading(true);
        setError(null);

        const feedbackList = await fetchFeedback();

        // Convert completed_on strings to Date objects
        const feedbackWithDates = feedbackList.map(feedback => ({
          ...feedback,
          completed_on: new Date(feedback.completed_on),
          transcript_id: feedback.transcript_id
        }));

        setFeedbackData(feedbackWithDates);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load feedback');
      } finally {
        setLoading(false);
      }
    };

    loadFeedback();
  }, []);

  return (
    <Container maxWidth="xl" sx={{ py: 4, overflow: 'auto' }}>
      <PageHeader
        title="Feedback Hub"
        subtitle="Review feedback from previous roleplay sessions"
        onBack={handleBack}
        showBackButton={true}
      />

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      )}

      {!loading && !error && feedbackData.length === 0 && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography variant="h6" color="text.secondary">
            No feedback available
          </Typography>
        </Box>
      )}

      {!loading && !error && feedbackData.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            width: '100%',
          }}
        >
          {feedbackData.map((feedbackItem) => (
            <FeedbackCard
              key={feedbackItem.id}
              feedbackId={feedbackItem.id}
              title={`Assessment: ${feedbackItem.scenario_title}`}
              description="Assessment feedback"
              completed={feedbackItem.completed_on}
              transcript_id={feedbackItem.transcript_id}
            />
          ))}
        </Box>
      )}
    </Container>
  );
};

export default FeedbackPage;